import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import i18next from '@setup/locales/i18n';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import { isEmptyDataWorkWithFilter, removeCurrentWork, setCurrentWork, setLoadingWork } from './work-slice';

import type { BusinessCardType, RootWorkData, UpdateBusinessCardType } from './work-types';
import type { EntityWork } from '../Work';
import type { RootMeta } from '@pages/hubs/Garage/store/types';
import type { GetHubsParams } from '@pages/hubs/Pets/store/pets-types';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { EntityHubSocket } from '@shared/models/hubs/hub-socket-response';
import type { AppDispatch, RootState } from '@store/store';

export const getWorks = createAsyncThunk<
  { data: RootWorkData[]; meta: RootMeta },
  Partial<GetHubsParams>,
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('getWorks', async (worksParams, { dispatch, rejectWithValue }) => {
  dispatch(setLoadingWork({ isLoading: false }));
  try {
    const { data, meta } = await api.work.getWorks({
      page: worksParams.page || 1,
      ...worksParams,
    });
    dispatch(isEmptyDataWorkWithFilter({ filters: worksParams, worksData: data }));

    return {
      data,
      meta,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setLoadingWork({ isLoading: true }));
  }
});

export const createWork = createAsyncThunk<RootWorkData, Partial<RootWorkData>, { rejectValue: ErrorType }>(
  'createWork',
  async (data, { rejectWithValue }) => {
    try {
      return await api.work.createWork(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);
export const getBusinessCardByPublicURL = createAsyncThunk<
  BusinessCardType,
  string,
  { rejectValue: ErrorType }
>('getBusinessCardByID', async (publicURL, { rejectWithValue }) => {
  try {
    return await api.work.businessCardByPublicURL(publicURL);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});
export const getBusinessCardByID = createAsyncThunk<BusinessCardType, string, { rejectValue: ErrorType }>(
  'getBusinessCardByID',
  async (id, { rejectWithValue }) => {
    try {
      return await api.work.businessCardByID(id);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);

export const updateBusinessCard = createAsyncThunk<
  BusinessCardType,
  UpdateBusinessCardType,
  { rejectValue: ErrorType }
>('getBusinessCardByID', async (data, { rejectWithValue }) => {
  try {
    return await api.work.updateBusinessCard(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const updateWork = createAsyncThunk<
  RootWorkData,
  {
    data: Partial<
      Omit<RootWorkData, 'contacts'> & {
        contacts: { id: number }[];
        is_create_contact_list_contact?: boolean;
      }
    >;
    workID: number;
  },
  { rejectValue: ErrorType }
>('updateWork', async ({ data, workID }, { rejectWithValue }) => {
  try {
    return await api.work.updateWork(data, workID);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data as ErrorType);
  }
});

export const removeWork = createAsyncThunk<any, { workID: number }, { rejectValue: ErrorType }>(
  'removeWork',
  async ({ workID }, { rejectWithValue, dispatch }) => {
    try {
      return await api.work.removeWork(workID).then(() => {
        dispatch(removeCurrentWork({ workID }));
        NotificationService.success(i18next.t('work.notifications.removeWork'));
      });
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const getDraftWork = createAsyncThunk<RootWorkData, number, { rejectValue: ErrorType }>(
  'getDraftWork',
  async (workID, { rejectWithValue }) => {
    try {
      return await api.work.getWork(workID);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(
        e,
        {
          [StatusServerErrors.AccessDenied]: {
            message: i18next.t('work.notifications.workNotSharedWithYou'),
          },
          [StatusServerErrors.ItemNotFound]: {
            message: i18next.t('work.notifications.workNotFound'),
          },
        },
        {
          notificationVariant: 'info',
        },
      );

      return rejectWithValue(axiosError.data);
    }
  },
);

export const getWork = createAsyncThunk<RootWorkData, number, { rejectValue: ErrorType }>(
  'getWork',
  async (workID, { rejectWithValue, dispatch }) => {
    try {
      const work = await api.work.getWork(workID);
      dispatch(setCurrentWork({ work }));
      return work;
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(
        e,
        {
          [StatusServerErrors.AccessDenied]: {
            message: i18next.t('work.notifications.itemNotAvailable'),
          },
          [StatusServerErrors.ItemNotFound]: {
            message: i18next.t('work.notifications.workNotFound'),
          },
        },
        {
          notificationVariant: 'info',
        },
      );

      return rejectWithValue(axiosError.data);
    }
  },
);

// ---------------- web-socket ----------------
export const updateWorkWS = createAsyncThunk<
  {
    workWS: RootWorkData;
  },
  EntityWork,
  { rejectValue: ErrorType }
>('updateWorkWS', async (socketEvent, { rejectWithValue }) => {
  try {
    const updateWorkResponse = await api.work.getWork(socketEvent.entity_id);

    return {
      workWS: updateWorkResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const addWorkWS = createAsyncThunk<
  {
    addWorkWS: RootWorkData;
  },
  EntityHubSocket,
  { rejectValue: ErrorType }
>('addWorkWS', async (socketEvent, { rejectWithValue }) => {
  try {
    const addWorkResponse = await api.work.getWork(socketEvent.entity_id);

    return {
      addWorkWS: addWorkResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const acceptedWorkWS = createAsyncThunk<
  {
    acceptedWorkWS: RootWorkData;
  },
  number,
  { rejectValue: ErrorType }
>('acceptedWorkWS', async (workID, { rejectWithValue }) => {
  try {
    const acceptedWorkResponse = await api.work.getWork(workID);

    return {
      acceptedWorkWS: acceptedWorkResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
